/**
 * Pops open a match's live-video page when something happens on it (goal, VAR,
 * penalty — the same event set the bell alarms on, see alerts.ts ALERT_TYPES).
 *
 * Like the bell, this is a pure side effect on the signal stream `useLiveState()`
 * already holds — it opens no connection of its own. The URL is the per-match
 * `videoUrl` the operator pasted in the watch-list; a match without one never
 * opens anything.
 *
 * Two things the browser forces on us:
 *  - `window.open` outside a user gesture is popup-blocked unless the operator
 *    allowed pop-ups for this origin. A blocked open is NOT dropped: it surfaces
 *    in `blocked` so the UI can offer a one-click "Apri" (a real gesture, always
 *    allowed).
 *  - Opening tabs on a phone is hostile, so this is desktop-only.
 */
import { useEffect, useRef, useState } from "react";
import type { MatchSignal, WatchGroup } from "../types.ts";
import { ALERT_TYPES } from "./alerts.ts";

const ENABLED_KEY = "autoOpenEnabled";
/** normalized video URL → epoch ms of the last tab we opened for it. */
const LAST_AT_KEY = "autoOpenLastAt";
/** A burst of events that points at one video URL (even through different match
 *  ids or sources) must cost ONE tab, not one tab per event. */
export const DEDUP_MS = 120_000;
/** Nothing older than this opens a tab. A goal reaches the panel in seconds, so
 *  anything staler is history replayed after a disconnect — the same reasoning
 *  as the bot's own MAX_SIGNAL_AGE_SECONDS freshness guard. Kept generous to
 *  absorb clock skew between the server and this browser. */
const MAX_AGE_MS = 120_000;
/** the phone breakpoint styles.css uses for the manual console */
const PHONE = "(max-width: 760px)";

export type BlockedOpen = { id: string; label: string; url: string };

/** Treat equivalent spellings of the same absolute URL as one video page. Keep
 *  the hash and query string: either may select a different stream. */
export function videoDedupKey(url: string): string {
  const trimmed = url.trim();
  try {
    return new URL(trimmed).href;
  } catch {
    return trimmed;
  }
}

export function wasVideoOpenedRecently(lastAt: Record<string, number>, url: string, now: number): boolean {
  const at = lastAt[videoDedupKey(url)];
  return typeof at === "number" && now - at <= DEDUP_MS;
}

function readLastAt(): Record<string, number> {
  try {
    const raw = JSON.parse(localStorage.getItem(LAST_AT_KEY) ?? "{}");
    return raw && typeof raw === "object" ? (raw as Record<string, number>) : {};
  } catch {
    return {};
  }
}

/** Persisted (not a ref) so the 2-minute window survives a page reload and is
 *  shared between two panel tabs — otherwise a reload during a busy match, or a
 *  second open tab, would re-open everything. */
function writeLastAt(map: Record<string, number>): void {
  try {
    localStorage.setItem(LAST_AT_KEY, JSON.stringify(map));
  } catch {
    /* quota / private mode — dedup degrades to per-session, never fatal */
  }
}

/** Named target so a re-open of the SAME match reuses its window instead of
 *  stacking a new one on top (belt and braces with the dedup window). */
export function openVideo(id: string, url: string): boolean {
  try {
    return window.open(url, `video-${id}`, "noopener") != null;
  } catch {
    return false;
  }
}

export function useAutoOpen(signals: MatchSignal[], watchGroups: WatchGroup[]) {
  const [enabled, setEnabledState] = useState(() => localStorage.getItem(ENABLED_KEY) === "1");
  const [blocked, setBlocked] = useState<BlockedOpen[]>([]);
  // Everything already delivered is marked seen, so a WS reconnect (which
  // re-sends ids we already hold) is silent. On a page load `signals` is still
  // empty at mount — the backlog lands in the FIRST effect run — so that first
  // batch only seeds `seen`, it never opens anything.
  const seen = useRef<Set<string>>(new Set(signals.map((s) => s.id)));
  const primed = useRef(false);
  const groupsRef = useRef(watchGroups);
  groupsRef.current = watchGroups;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    const fresh = signals.filter((s) => !seen.current.has(s.id));
    if (fresh.length === 0) return;
    for (const s of fresh) seen.current.add(s.id);
    if (!primed.current) {
      primed.current = true; // the hello backlog: seed only
      return;
    }
    if (!enabledRef.current) return;
    if (window.matchMedia(PHONE).matches) return; // desktop only

    const now = Date.now();
    const lastAt = readLastAt();
    // prune, so the map can't grow with every match ever watched
    for (const [url, at] of Object.entries(lastAt)) if (now - at > DEDUP_MS) delete lastAt[url];

    const justBlocked: BlockedOpen[] = [];
    const attemptedUrls = new Set<string>();
    let touched = false;
    for (const s of fresh) {
      if (!ALERT_TYPES.has(s.type)) continue;
      if (now - Date.parse(s.ts) > MAX_AGE_MS) continue; // replayed history, not something happening now
      const group = groupsRef.current.find((g) => g.id === s.match);
      if (!group?.enabled || !group.videoUrl) continue;
      const urlKey = videoDedupKey(group.videoUrl);
      if (wasVideoOpenedRecently(lastAt, group.videoUrl, now) || attemptedUrls.has(urlKey)) continue;
      attemptedUrls.add(urlKey);

      if (openVideo(group.id, group.videoUrl)) {
        lastAt[urlKey] = now;
        touched = true;
      } else {
        // popup-blocked: do NOT stamp lastAt, so the next event can retry, and
        // hand the match to the UI so the operator can open it with one click
        justBlocked.push({ id: group.id, label: group.label ?? group.id, url: group.videoUrl });
      }
    }
    if (touched) writeLastAt(lastAt);
    if (justBlocked.length) {
      setBlocked((prev) => [...prev.filter((b) => !justBlocked.some((j) => videoDedupKey(j.url) === videoDedupKey(b.url))), ...justBlocked]);
    }
  }, [signals]);

  const setEnabled = (v: boolean) => {
    localStorage.setItem(ENABLED_KEY, v ? "1" : "0");
    setEnabledState(v);
    if (!v) setBlocked([]);
  };

  const dismissBlocked = (id: string) => setBlocked((prev) => prev.filter((b) => b.id !== id));

  return { enabled, setEnabled, blocked, dismissBlocked };
}
